/**
 * API Fetch Utility
 *
 * Provides a consistent fetch wrapper that:
 * - Prepends VITE_API_HOST to relative /api URLs in production
 * - Falls back to relative URLs for dev mode (handled by Vite proxy)
 * - Includes authorization headers
 * - Handles common error cases
 * - Auto-refreshes expired tokens on 401
 */

import { supabase } from './supabase';
import { isE2EAuthBypassActive, getE2EBypassEmail, E2E_BYPASS_HEADER } from './e2eAuth';
import { ApiError, buildApiError, buildNetworkError } from './apiError';

export { ApiError } from './apiError';
export { toUserMessage } from './apiError';

const API_HOST = import.meta.env.VITE_API_HOST || '';

// Singleton refresh promise — prevents multiple concurrent token refresh calls
let refreshPromise: Promise<string | null> | null = null;

/**
 * Registered by AuthContext so apiFetch can flip `mfaRequired` on ANY API
 * call, not just the login-time challenge check. The backend's MfaGuard
 * enforces MFA on every request once ENABLE_MFA is on — previously nothing
 * client-side reacted to that, so a stale tab (or a session where the
 * verification TTL lapsed) would silently 403 forever instead of routing
 * back to /mfa-verify.
 */
type MfaRequiredHandler = () => void;
let mfaRequiredHandler: MfaRequiredHandler | null = null;

export function setMfaRequiredHandler(handler: MfaRequiredHandler | null): void {
  mfaRequiredHandler = handler;
}

async function refreshToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = supabase.auth
      .refreshSession()
      .then(({ data, error }) => {
        if (error || !data?.session?.access_token) return null;
        return data.session.access_token;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/**
 * Construct full API URL
 * In production: prepends VITE_API_HOST to /api paths
 * In dev: relative URLs work via Vite proxy
 */
function getApiUrl(path: string): string {
  if (!path.startsWith('/api')) {
    throw new Error(`API path must start with /api, got: ${path}`);
  }

  // In production with VITE_API_HOST set, use full URL
  // Otherwise fall back to relative URL (Vite proxy in dev)
  if (API_HOST) {
    return `${API_HOST}${path}`;
  }

  return path;
}

/**
 * Fetch from API endpoint with optional authorization
 *
 * @param path - API endpoint path (must start with /api)
 * @param options - Fetch options, will merge with auth headers if token provided
 * @param token - Optional authorization token
 * @param retryCount - Internal: number of retry attempts
 * @returns Response from fetch
 */
export async function apiFetch(
  path: string,
  options?: RequestInit,
  token?: string,
  retryCount = 0
): Promise<Response> {
  const url = getApiUrl(path);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(typeof options?.headers === 'object' && options?.headers ? (options.headers as Record<string, string>) : {}),
  };

  // E2E bypass: replace the real Authorization flow with the impersonation
  // header the backend guard understands. `token` (a fabricated session's
  // access_token — see contexts/AuthContext.tsx) is intentionally ignored
  // here; there is no real JWT to send or refresh in bypass mode.
  if (isE2EAuthBypassActive()) {
    headers[E2E_BYPASS_HEADER] = getE2EBypassEmail();
  } else if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch (err) {
    // fetch only rejects on transport failures (offline, DNS, CORS, abort).
    // Left unwrapped these surfaced as "Failed to fetch" / "The operation was
    // aborted", which tell a user nothing actionable.
    throw buildNetworkError(err);
  }

  // If 401 and we haven't retried yet, try refreshing the session.
  // Uses a singleton promise so concurrent 401s share a single refresh call.
  // Skipped in bypass mode: there is no Supabase session to refresh.
  if (
    response.status === 401 &&
    retryCount === 0 &&
    token &&
    !isE2EAuthBypassActive()
  ) {
    try {
      const newToken = await refreshToken();
      if (newToken) {
        return apiFetch(path, options, newToken, retryCount + 1);
      }
    } catch (err) {
      console.error('[API] Token refresh failed, will return 401:', err);
    }
  }

  // Backend MfaGuard denies with 403 + {error: "MFA_REQUIRED"} when
  // ENABLE_MFA is on and this session has no live MFA verification.
  // The MFA challenge/verify endpoints themselves are exempt server-side
  // (@SkipMfaGuard), so this never fires for them.
  if (response.status === 403 && mfaRequiredHandler) {
    try {
      const body = await response.clone().json();
      if (body?.error === 'MFA_REQUIRED') {
        mfaRequiredHandler();
      }
    } catch {
      // Not every 403 has a JSON body — ignore parse failures.
    }
  }

  return response;
}

/**
 * Fetch and parse JSON from API endpoint
 *
 * @param path - API endpoint path
 * @param options - Fetch options
 * @param token - Optional authorization token
 * @returns Parsed JSON response
 */
export async function apiJson<T = unknown>(
  path: string,
  options?: RequestInit,
  token?: string
): Promise<T> {
  const response = await apiFetch(path, options, token);

  // Map the failure onto a message fit to render. The raw body stays on
  // ApiError.detail for the console and for callers that branch on .code.
  if (!response.ok) {
    throw buildApiError(response.status, await response.text());
  }

  // Guard against HTML or other non-JSON responses (e.g., SPA index.html),
  // which in practice means the request hit the frontend server instead of
  // the backend — a deployment problem, so keep the diagnostic in `detail`.
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const bodyPreview = (await response.text()).slice(0, 200);
    throw new ApiError(
      'Something went wrong on our end. Please try again in a moment.',
      {
        status: response.status,
        code: 'NON_JSON_RESPONSE',
        detail:
          `Non-JSON response (${contentType}) from ${response.url}. ` +
          `Verify VITE_API_HOST points at the backend.\nPreview: ${bodyPreview}`,
      }
    );
  }

  return response.json();
}

/**
 * Debug: Log actual API host being used
 */
export function getApiHost(): string {
  return API_HOST || '(relative URLs via Vite proxy)';
}
